import React from "react";
import { MessageSquareText, Wrench, ShieldAlert, BellRing, ArrowUpRight } from "lucide-react";
import { trackEvent } from "../utils/analytics";

const JOBS = [
  { icon: MessageSquareText, title: "Resident concierge", body: "Residents text one number and get instant, cited answers straight from your community's governing documents — 24/7." },
  { icon: Wrench, title: "Maintenance in seconds", body: "A resident texts a photo of a broken gate or a leak; it becomes a routed, logged work order and your team is notified instantly." },
  { icon: ShieldAlert, title: "Neighborhood Watch", body: "See something off? Text it. Redline's live security team pulls up the cameras, assesses, and dispatches or escalates in real time." },
  { icon: BellRing, title: "Community-wide alerts", body: "Push a safety or operations alert — \u201cgate down, use the east entrance\u201d — to every opted-in resident's phone in seconds." },
];

const CHAT = [
  { from: "resident", text: "There's water leaking from the unit above me in 407." },
  { from: "redline", text: "Stay safe first \u2014 the main shut-off is in the ground-floor utility room (per Rules §7). I've logged an urgent maintenance ticket and notified the on-call team. \u{1F6A8}" },
  { from: "resident", text: "There's a car that's been parked by the gate for hours." },
  { from: "redline", text: "Thanks \u2014 our security team is pulling up the gate cameras now and will check it out." },
];

const AskRedline = () => {
  const openSite = () => {
    trackEvent("askredline_cta_click", {
      category: "cross_sell", action: "outbound_click",
      label: "askredline_site", value: 8, contact_method: "external_link",
    });
  };

  return (
    <section id="askredline" className="relative bg-[#0B1120] text-white py-24 lg:py-32 overflow-hidden">
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#D32F2F]/20 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 -left-20 w-[420px] h-[420px] rounded-full bg-[#1D4ED8]/15 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left — copy */}
          <div data-reveal>
            <div className="inline-flex items-center gap-2 mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B6B]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5252] pulse-dot" /> New · AskRedline
            </div>
            <h2 className="font-head text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              A concierge that never sleeps — one text away.
            </h2>
            <p className="text-white/60 mt-6 text-lg leading-relaxed">
              Beyond the cameras, your residents get a dedicated text line. They ask anything, report a
              maintenance issue with a photo, or flag a safety concern — and Redline's team is right there,
              backed by the same command center that watches your community.
            </p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7 mt-10">
              {JOBS.map((j) => (
                <div key={j.title} className="flex gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                    <j.icon className="w-5 h-5 text-[#FF6B6B]" />
                  </span>
                  <div>
                    <h3 className="font-head text-[15px] font-semibold text-white mb-1.5">{j.title}</h3>
                    <p className="text-[13.5px] text-white/55 leading-relaxed">{j.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a href="https://askredline.com" target="_blank" rel="noopener noreferrer" onClick={openSite}
                 data-testid="askredline-cta"
                 className="group inline-flex items-center gap-2 rounded-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white text-sm font-medium px-7 py-3.5 transition-all hover:scale-[1.02]">
                Explore AskRedline
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <span className="text-[13px] text-white/45">No app to install — residents just text.</span>
            </div>
          </div>

          {/* Right — phone mockup */}
          <div data-reveal data-reveal-delay="1" className="flex justify-center lg:justify-end">
            <div className="relative w-[300px] sm:w-[330px]">
              <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-b from-white/10 to-transparent blur-md" />
              <div className="relative rounded-[2.4rem] border border-white/12 bg-[#0F172A] p-3 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]">
                <div className="rounded-[1.9rem] bg-[#111827] overflow-hidden">
                  {/* status bar */}
                  <div className="flex items-center justify-between px-6 pt-4 pb-2 text-[11px] text-white/50 font-medium">
                    <span>9:41</span>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Active now
                    </span>
                  </div>
                  {/* header */}
                  <div className="px-5 pb-4 border-b border-white/[0.06]">
                    <p className="font-head text-sm font-semibold text-white">Ask Redline</p>
                    <p className="text-[11px] text-white/40">replies instantly</p>
                  </div>
                  {/* messages */}
                  <div className="px-4 py-5 space-y-3">
                    {CHAT.map((m, i) => (
                      <div key={i} className={`flex ${m.from === "resident" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] px-3.5 py-2.5 text-[12.5px] leading-snug rounded-2xl ${
                          m.from === "resident"
                            ? "bg-[#2563EB] text-white rounded-br-md"
                            : "bg-white/[0.08] text-white/85 rounded-bl-md"
                        }`}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* input */}
                  <div className="px-4 pb-5">
                    <div className="flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-4 py-2.5">
                      <span className="text-[12.5px] text-white/35">Text message…</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AskRedline;
